import React, { FC, useEffect, useRef, useState } from 'react';

import { Notification } from 'api-client';
import { ChevronIcon } from 'components/common/icons';
import { cn } from 'utils';

import styles from './NotificationContent.module.scss';

interface Props {
  notification: Notification;
}

const COLLAPSED_MAX_HEIGHT = 200;

const NotificationContent: FC<Props> = ({ notification }) => {
  const innerElement = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(COLLAPSED_MAX_HEIGHT);

  useEffect(() => {
    const element = innerElement.current;
    if (element === null) {
      return;
    }

    const updateHeight = () => setContentHeight(element.scrollHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [notification.content]);

  const collapsible = contentHeight > COLLAPSED_MAX_HEIGHT;
  const collapsed = collapsible && !expanded;

  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a') !== null) {
      return;
    }

    if (collapsed) {
      setExpanded(true);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={cn(styles.content, collapsed && styles.collapsed)}
        style={{ maxHeight: collapsed ? COLLAPSED_MAX_HEIGHT : contentHeight }}
        onClick={handleContentClick}
      >
        <div
          className={styles.inner}
          ref={innerElement}
          dangerouslySetInnerHTML={{ __html: notification.content }}
        />
      </div>

      {collapsible && (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span>{expanded ? 'Свернуть' : 'Развернуть'}</span>
          <ChevronIcon direction={expanded ? 'down' : 'up'} size={18} />
        </button>
      )}
    </div>
  );
};

export default NotificationContent;
